import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { getProcessingFeeLineItem } from '../../../../helpers/invoices';
import { currencyFormatter } from '../../../../helpers/utils';
import DateTime from '../../../DateTime/DateTime';
import { getAmountDue, getInvoiceTotal, getPaidAmount } from './InvoiceDetailUtils';

export function InvoiceDetailSummarySection({
  billedTo,
  selectedInvoice,
  invoiceRows,
  hasInvoiceBeenPaid,
}: {
  billedTo: string;
  selectedInvoice: any;
  invoiceRows: JSX.Element[];
  hasInvoiceBeenPaid: boolean;
}) {
  const { t } = useTranslation();
  const { paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer } = useFlags();
  const paidAmount = getPaidAmount(selectedInvoice, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer);
  const amountDue = getAmountDue(selectedInvoice, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer);
  const invoiceTotal = getInvoiceTotal(selectedInvoice, paymentsAreProcessingFeesHiddenWhenFamilyIsThePayer);
  const processingFeeLineItem = hasInvoiceBeenPaid ? getProcessingFeeLineItem(selectedInvoice.invoiceItemList) : null;

  return (
    <div className="mt-4 grid grid-cols-1 gap-y-4">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <div className="justify-content-between grid grid-cols-2">
          <span data-testid="invoice-detail-billed-to">{t('billing.invoiceDetail.summary.billedToLabel')}</span>
          <span data-testid="invoice-detail-billed-to-value">{billedTo}</span>
        </div>
        <div className="justify-content-between grid grid-cols-2">
          <span data-testid="invoice-detail-invoice-number">
            {t('billing.invoiceDetail.summary.invoiceNumberLabel')}
          </span>
          <span data-testid="invoice-detail-invoice-number-value">{selectedInvoice && selectedInvoice.id}</span>
        </div>
        <div className="justify-content-between grid grid-cols-2">
          <span data-testid="invoice-detail-invoice-date">{t('billing.invoiceDetail.summary.dueDateLabel')}</span>
          <span data-testid="invoice-detail-invoice-date-value">
            {selectedInvoice && selectedInvoice.dateDue && (
              <DateTime epoch={selectedInvoice.dateDue} format={'MM/DD/YYYY'} />
            )}
          </span>
        </div>
        <div className="justify-content-between grid grid-cols-2">
          <span data-testid="invoice-detail-invoice-notes">{t('billing.invoiceDetail.summary.notesLabel')}</span>
          <span className="invoice-note" data-testid="invoice-detail-invoice-notes-value">
            {selectedInvoice.notes || t('billing.invoiceDetails.thankYouMessage')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1">
        <table className="text-left">
          <thead>
            <tr>
              <th data-testid="invoice-detail-category">{t('billing.invoiceDetail.summary.categoryColumn')}</th>
              <th data-testid="invoice-detail-item">{t('billing.invoiceDetail.summary.itemColumn')}</th>
              <th data-testid="invoice-detail-notes">{t('billing.invoiceDetail.summary.notesColumn')}</th>
              <th data-testid="invoice-detail-amount" className="text-right">
                {t('billing.invoiceDetail.summary.amountColumn')}
              </th>
            </tr>
          </thead>
          <tbody className="border-t">{invoiceRows}</tbody>
        </table>

        <table className="text-right">
          <tbody>
            <tr>
              <td />
              <td />
              <td data-testid="invoice-detail-total" className="text-right">
                {t('billing.invoiceDetail.summary.totalLabel')}
              </td>
              <td data-testid="invoice-detail-total-value" className="text-right">
                {selectedInvoice &&
                  currencyFormatter(invoiceTotal, {
                    precision: 2,
                  })}
              </td>
            </tr>
            <tr>
              <td />
              <td />
              <td data-testid="invoice-detail-amount-paid" className="text-right">
                {processingFeeLineItem
                  ? t('billing.invoiceDetail.summary.amountPaidByFamilyLabel')
                  : t('billing.invoiceDetails.amountPaid')}
              </td>
              <td data-testid="invoice-detail-amount-paid-value" className="text-right">
                {selectedInvoice &&
                  currencyFormatter(paidAmount, {
                    precision: 2,
                  })}
              </td>
            </tr>
            <tr>
              <td />
              <td />
              <td data-testid="invoice-detail-amount-due" className="text-right">
                {t('billing.invoiceDetails.amountDue')}
              </td>
              <td data-testid="invoice-detail-amount-due-value" className="text-right">
                {selectedInvoice &&
                  currencyFormatter(amountDue, {
                    precision: 2,
                  })}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
